import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Car, User, Send, MessageCircle, Package, CheckCircle2, Undo2, Plus, Trash2, Droplets, Wrench, ShieldCheck, Sparkles, CalendarClock, ClipboardList } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AtelierLayout from '@/components/atelier/AtelierLayout';
import { LIQUIDES_ITEMS, LIQUIDE_LEVELS, SERVICE_PLUS_ITEMS } from '@/components/workorder/constants';
import { OrderSlipsSection } from '@/components/workorder/OrderSlipsSection';
import { AttachmentsSection } from '@/components/workorder/AttachmentsSection';
import { Textarea } from '@/components/ui/textarea';
import { MechanicsPicker, MechanicTimers, isMechanic, empName, assignedIds } from '@/components/workorder/atelier/MechanicsPanel';
import { TodoBlock } from '@/components/workorder/atelier/TodoBlock';
import { FluidsSection } from '@/components/workorder/atelier/FluidsSection';
import { ReadySummary } from '@/components/workorder/atelier/ReadySummary';
import { ClientContextTabs } from '@/components/workorder/ClientContextTabs';

const STATUS_CONFIG = {
  open: { label: 'A venir', cls: 'bg-blue-100 text-blue-700' },
  signed: { label: 'Signee', cls: 'bg-emerald-100 text-emerald-700' },
  in_progress: { label: 'En cours', cls: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Terminee', cls: 'bg-slate-100 text-slate-600' },
};

const CHECKLIST_LABELS = {
  service_standard: 'Service standard',
  grand_service: 'Grand service',
  changement_roues: 'Changement de roues',
  montage_pneus: 'Montage pneus',
  controle_expertise: 'Controle avant expertise',
  preparation_expertise: 'Preparation expertise',
  presentation_expertise: 'Presentation expertise',
  diagnostic: 'Diagnostic',
  reparation: 'Reparation',
  peinture: 'Peinture',
  cas_assurance: 'Cas assurance',
};

const PART_CATEGORIES = [
  { value: 'piece', label: 'Piece', icon: Wrench },
  { value: 'liquide', label: 'Liquide', icon: Droplets },
  { value: 'consommable', label: 'Consommable', icon: Package },
];

const AtelierWorkOrderDetail = ({ onLogout }) => {
  const { woId } = useParams();
  const navigate = useNavigate();
  const [wo, setWo] = useState(null);
  const [parts, setParts] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [markingReady, setMarkingReady] = useState(false);
  const [partSearch, setPartSearch] = useState('');

  // New part form
  const [showAddPart, setShowAddPart] = useState(false);
  const [newPart, setNewPart] = useState({ part_name: '', reference: '', quantity: 1, category: 'piece' });
  const [predefinedCategories, setPredefinedCategories] = useState([]);
  const [selectedPredefinedCat, setSelectedPredefinedCat] = useState(null);
  const [employees, setEmployees] = useState([]);

  // Liquides
  const [liquides, setLiquides] = useState({});

  const loadData = useCallback(async () => {
    try {
      const [woRes, partsRes, timeRes, predefinedRes, empRes] = await Promise.all([
        axios.get(`${API}/work-orders/${woId}`),
        axios.get(`${API}/atelier/work-orders/${woId}/parts`),
        axios.get(`${API}/atelier/work-orders/${woId}/time-entries`),
        axios.get(`${API}/work-orders/predefined-parts`).catch(() => ({ data: [] })),
        axios.get(`${API}/employees`).catch(() => ({ data: [] })),
      ]);
      setWo(woRes.data);
      setParts(partsRes.data);
      setTimeEntries(timeRes.data);
      setPredefinedCategories(predefinedRes.data);
      setEmployees(empRes.data.filter(isMechanic));
      if (woRes.data?.liquides) setLiquides(woRes.data.liquides);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [woId]);

  useEffect(() => { loadData(); }, [loadData]);

  const mechanicNames = wo ? employees.filter(e => assignedIds(wo).includes(e.id)).map(empName) : [];
  const mechanicName = mechanicNames.join(', ');

  // ====== NOTES ======
  const sendNote = async () => {
    if (!note.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/atelier/work-orders/${woId}/note`, { note });
      toast.success('Note ajoutee');
      setNote('');
      loadData();
    } catch { toast.error('Erreur'); }
    finally { setSending(false); }
  };

  // ====== PARTS ======
  const addPart = async (partData) => {
    const data = partData || newPart;
    if (!data.part_name.trim()) { toast.error('Nom requis'); return; }
    try {
      await axios.post(`${API}/atelier/work-orders/${woId}/parts`, {
        part_name: data.part_name,
        reference: data.reference || '',
        quantity: data.quantity || 1,
        unit_price: 0,
        category: data.category || 'piece',
        added_by: mechanicName || 'Atelier',
      });
      toast.success('Piece ajoutee');
      setNewPart({ part_name: '', reference: '', quantity: 1, category: 'piece' });
      setShowAddPart(false);
      setSelectedPredefinedCat(null);
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const addPredefinedPart = async (item) => {
    try {
      await axios.post(`${API}/atelier/work-orders/${woId}/parts`, {
        part_name: item.name,
        reference: item.reference || '',
        quantity: 1,
        unit_price: 0,
        category: 'piece',
        added_by: mechanicName || 'Atelier',
      });
      toast.success(`${item.name} ajoutee`);
      loadData();
      // Keep the panel open - don't close showAddPart or selectedPredefinedCat
    } catch { toast.error('Erreur'); }
  };

  // Liquides
  const updateLiquide = async (key, value) => {
    const updated = { ...liquides, [key]: value };
    setLiquides(updated);
    try { await axios.put(`${API}/work-orders/${woId}`, { liquides: updated }); } catch { /* auto-save */ }
  };

  const deletePart = async (partId) => {
    try {
      await axios.delete(`${API}/atelier/work-orders/${woId}/parts/${partId}`);
      toast.success('Piece supprimee');
      loadData();
    } catch { toast.error('Erreur'); }
  };

  // ====== VEHICLE READY ======
  const markVehicleReady = async () => {
    if (!mechanicName.trim()) { toast.error('Choisissez le mécanicien en haut de la fiche'); return; }
    setMarkingReady(true);
    try {
      await axios.post(`${API}/atelier/work-orders/${woId}/vehicle-ready`, { mechanic_name: mechanicName.trim() });
      toast.success('Vehicule marque comme pret !');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setMarkingReady(false); }
  };

  const undoVehicleReady = async () => {
    setMarkingReady(true);
    try {
      await axios.post(`${API}/atelier/work-orders/${woId}/undo-vehicle-ready`);
      toast.success('Statut annule');
      loadData();
    } catch { toast.error('Erreur'); }
    finally { setMarkingReady(false); }
  };

  if (loading) return <AtelierLayout onLogout={onLogout}><p className="text-center text-slate-400 py-20">Chargement...</p></AtelierLayout>;
  if (!wo) return <AtelierLayout onLogout={onLogout}><p className="text-center text-slate-400 py-20">Fiche non trouvee</p></AtelierLayout>;

  const st = STATUS_CONFIG[wo.status] || STATUS_CONFIG.open;
  const atelierNotes = wo.atelier_notes || [];
  const spIds = Array.isArray(wo.service_plus) ? wo.service_plus : Object.keys(wo.service_plus || {}).filter(k => wo.service_plus[k]);
  const q = partSearch.trim().toLowerCase();
  const searchHits = q ? predefinedCategories.flatMap(c => c.items.filter(it => `${it.name} ${it.reference || ''}`.toLowerCase().includes(q))).slice(0, 20) : [];

  return (
    <AtelierLayout onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-4" data-testid="atelier-wo-detail">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-slate-900">{wo.number}</h1>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
              {wo.billing_type === 'devis' && (
                <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-amber-100 text-amber-700 border border-amber-300" data-testid="billing-type-badge">DEVIS</span>
              )}
              {wo.billing_type === 'reparation' && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700" data-testid="billing-type-badge">Réparation</span>
              )}
            </div>
            <p className="text-sm text-slate-500">{wo.appointment_date ? new Date(wo.appointment_date).toLocaleDateString('fr-CH') : ''}</p>
          </div>
        </div>

        <MechanicsPicker woId={woId} wo={wo} employees={employees} onChanged={loadData} />

        {/* Client & Vehicle */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-violet-500" />
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Client</h3>
            </div>
            <p className="font-semibold text-slate-900">{wo.client_name}</p>
            {wo.client_phone && <p className="text-sm text-slate-500">{wo.client_phone}</p>}
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Car className="w-4 h-4 text-blue-500" />
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Vehicule</h3>
            </div>
            <p className="font-semibold text-slate-900">
              {wo.vehicle_brand && wo.vehicle_model ? `${wo.vehicle_brand} ${wo.vehicle_model}` : wo.vehicle_info || 'Non renseigne'}
            </p>
            <div className="flex gap-3 text-sm text-slate-500">
              {wo.vehicle_plate && <span className="font-mono">{wo.vehicle_plate}</span>}
              {wo.vehicle_km && <span>{wo.vehicle_km} km</span>}
            </div>
          </Card>
        </div>

        {/* Reprise vehicule */}
        {(wo.pickup_date || wo.pickup_time) && (
          <Card className="p-4 border-2 border-emerald-200 bg-emerald-50/30" data-testid="atelier-pickup-section">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-emerald-600" />
              <div>
                <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Reprise du vehicule</h3>
                <p className="text-base font-bold text-emerald-700 mt-0.5">
                  {wo.pickup_date ? (() => {
                    const d = new Date(wo.pickup_date);
                    const jours = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
                    const mois = ['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'];
                    return `${jours[d.getDay()]} ${d.getDate()} ${mois[d.getMonth()]} ${d.getFullYear()}`;
                  })() : ''}{wo.pickup_time ? ` a ${wo.pickup_time}` : ''}
                </p>
              </div>
            </div>
          </Card>
        )}

        <TodoBlock wo={wo} />

        <ClientContextTabs clientId={wo.client_id} excludeWo={woId} />

        <MechanicTimers woId={woId} names={mechanicNames} entries={timeEntries} onChanged={loadData} />

        <AttachmentsSection woId={woId} workOrder={wo} isAtelier={true} />

        {/* Service Plus (nettoyage moteur, chassis, etc.) */}
        <Card className="p-4" data-testid="service-plus-section">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-violet-500" />
            <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Services additionnels</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {SERVICE_PLUS_ITEMS.map(item => {
              const isChecked = spIds.includes(item.id);
              return (
                <label key={item.id} className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg border transition-colors ${isChecked ? 'border-violet-400 bg-violet-50 font-semibold' : 'border-transparent hover:bg-slate-50'}`}>
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={async (checked) => {
                      const updated = checked ? [...spIds, item.id] : spIds.filter(x => x !== item.id);
                      setWo(prev => ({ ...prev, service_plus: updated }));
                      try { await axios.put(`${API}/work-orders/${woId}`, { service_plus: updated }); }
                      catch { toast.error('Erreur sauvegarde'); }
                    }}
                    data-testid={`sp-${item.id}`}
                  />
                  <span className="text-sm text-slate-700">{item.label}</span>
                </label>
              );
            })}
          </div>
        </Card>

        {/* Recommandations mécanicien */}
        <Card className="p-4" data-testid="recommendations-section">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList className="w-4 h-4 text-orange-500" />
            <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Recommandations</h3>
          </div>
          <p className="text-xs text-slate-400 mb-2">Notez ici ce qui est recommandé de changer ou à faire</p>
          <Textarea
            value={wo.mechanic_recommendations || ''}
            onChange={(e) => setWo(prev => ({ ...prev, mechanic_recommendations: e.target.value }))}
            onBlur={async (e) => {
              try { await axios.put(`${API}/work-orders/${woId}`, { mechanic_recommendations: e.target.value }); }
              catch { toast.error('Erreur sauvegarde'); }
            }}
            rows={3}
            placeholder="Ex: Plaquettes de frein avant usées, silent-bloc à remplacer..."
            className="text-sm"
            data-testid="recommendations-textarea"
          />
        </Card>

        {/* ====== PIECES / CONSOMMABLES ====== */}
        <Card className="p-4" data-testid="parts-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-blue-500" />
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Pieces / Consommables ({parts.length})
              </h3>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowAddPart(!showAddPart)} data-testid="toggle-add-part">
              <Plus className="w-4 h-4 mr-1" /> Ajout libre
            </Button>
          </div>

          <div className="relative mb-2">
            <Input value={partSearch} onChange={e => setPartSearch(e.target.value)} placeholder="Rechercher une pièce (ex : filtre, plaquettes, 5W30…)" className="text-sm" data-testid="part-search-input" />
          </div>
          {q && (
            <div className="grid grid-cols-2 gap-1.5 mb-3" data-testid="part-search-results">
              {searchHits.map((item, j) => (
                <button key={j} type="button" onClick={() => addPredefinedPart(item)} className="text-left px-3 py-2 rounded border bg-white border-slate-200 hover:border-blue-300 hover:bg-blue-50" data-testid={`part-search-hit-${j}`}>
                  <span className="text-xs font-medium text-slate-800 block">{item.name}</span>{item.reference && <span className="text-[10px] text-slate-400">{item.reference}</span>}
                </button>))}
              {searchHits.length === 0 && <p className="text-xs text-slate-500 col-span-2">Aucune pièce trouvée — utilisez « Ajout libre ».</p>}
            </div>
          )}

          {/* Predefined parts categories */}
          {!q && predefinedCategories.length > 0 && (
            <div className="mb-3 space-y-2" data-testid="predefined-parts-atelier">
              <p className="text-xs text-slate-500 font-medium">Pieces courantes :</p>
              <div className="flex flex-wrap gap-1.5">
                {predefinedCategories.map((cat, i) => (
                  <button key={i} type="button" onClick={() => setSelectedPredefinedCat(selectedPredefinedCat === cat.category ? null : cat.category)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      selectedPredefinedCat === cat.category ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                    }`} data-testid={`atelier-cat-${cat.category.replace(/\s/g,'-').toLowerCase()}`}>
                    {cat.category}
                  </button>
                ))}
              </div>
              {selectedPredefinedCat && (
                <div className="grid grid-cols-2 gap-1.5">
                  {predefinedCategories.find(c => c.category === selectedPredefinedCat)?.items.map((item, j) => {
                    const alreadyAdded = parts.some(p => (p.part_name || p.name) === item.name);
                    return (
                      <button key={j} type="button"
                        onClick={() => addPredefinedPart(item)}
                        className={`text-left px-3 py-2 rounded border transition-colors ${
                          alreadyAdded
                            ? 'bg-emerald-50 border-emerald-300 hover:bg-emerald-100'
                            : 'bg-white border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                        data-testid={`atelier-predef-${item.reference || j}`}>
                        <span className="text-xs font-medium text-slate-800 block">{item.name}</span>
                        <div className="flex items-center justify-between">
                          {item.reference && <span className="text-[10px] text-slate-400">{item.reference}</span>}
                          {alreadyAdded && <span className="text-[10px] text-emerald-600 font-medium">Ajoutee</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Manual add part form (no price) */}
          {showAddPart && (
            <div className="mb-4 p-3 bg-slate-50 rounded-lg space-y-2" data-testid="add-part-form">
              <p className="text-xs text-slate-500 font-medium">Ajout libre :</p>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={newPart.part_name}
                  onChange={e => setNewPart({ ...newPart, part_name: e.target.value })}
                  placeholder="Nom (ex: Filtre a huile)"
                  className="text-sm"
                  data-testid="part-name-input"
                />
                <Input
                  value={newPart.reference}
                  onChange={e => setNewPart({ ...newPart, reference: e.target.value })}
                  placeholder="Reference (optionnel)"
                  className="text-sm"
                  data-testid="part-ref-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500">Quantite</label>
                  <Input
                    type="number" min="1"
                    value={newPart.quantity}
                    onChange={e => setNewPart({ ...newPart, quantity: parseInt(e.target.value) || 1 })}
                    className="text-sm"
                    data-testid="part-qty-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500">Categorie</label>
                  <Select value={newPart.category} onValueChange={v => setNewPart({ ...newPart, category: v })}>
                    <SelectTrigger className="text-sm h-9" data-testid="part-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PART_CATEGORIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setShowAddPart(false); setSelectedPredefinedCat(null); }}>Annuler</Button>
                <Button size="sm" onClick={() => addPart()} data-testid="confirm-add-part">
                  <Plus className="w-3 h-3 mr-1" /> Ajouter
                </Button>
              </div>
            </div>
          )}

          {/* Parts list */}
          {parts.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-3">Aucune piece ajoutee</p>
          ) : (
            <div className="space-y-1">
              {parts.map(p => {
                const cat = PART_CATEGORIES.find(c => c.value === p.category);
                const CatIcon = cat?.icon || Package;
                return (
                  <div key={p.id} className={`flex items-center justify-between py-2 border-b border-slate-100 last:border-0 ${p.returned ? 'opacity-50' : ''}`} data-testid={`part-${p.id}`}>
                    <div className="flex items-center gap-2 flex-1">
                      <CatIcon className="w-4 h-4 text-slate-400 shrink-0" />
                      <div>
                        <p className={`text-sm font-medium text-slate-900 ${p.returned ? 'line-through' : ''}`}>{p.part_name || p.name}</p>
                        {p.reference && <p className="text-[10px] text-slate-400 font-mono">{p.reference}</p>}
                        {p.source === 'bulletin' && <p className="text-[10px] text-amber-700">Bulletin {p.supplier} {p.slip_number}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1 text-[11px] font-semibold text-red-700 cursor-pointer mr-1">
                        <input type="checkbox" checked={!!p.returned} data-testid={`part-return-${p.id}`} onChange={async (e) => {
                          try { await axios.put(`${API}/work-orders/${woId}/parts/${p.id}/returned`, { returned: e.target.checked }); loadData(); }
                          catch { toast.error('Erreur'); }
                        }} />Retour
                      </label>
                      <button
                        onClick={async () => {
                          const newQty = Math.max(1, (p.quantity || 1) - 1);
                          try {
                            await axios.put(`${API}/atelier/work-orders/${woId}/parts/${p.id}`, { quantity: newQty });
                            loadData();
                          } catch { toast.error('Erreur'); }
                        }}
                        className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-bold flex items-center justify-center"
                        data-testid={`part-minus-${p.id}`}
                      >-</button>
                      <span className="text-xs font-mono text-slate-700 w-6 text-center font-bold">{p.quantity}</span>
                      <button
                        onClick={async () => {
                          const newQty = (p.quantity || 1) + 1;
                          try {
                            await axios.put(`${API}/atelier/work-orders/${woId}/parts/${p.id}`, { quantity: newQty });
                            loadData();
                          } catch { toast.error('Erreur'); }
                        }}
                        className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-bold flex items-center justify-center"
                        data-testid={`part-plus-${p.id}`}
                      >+</button>
                      {!p.unit_price && <span className="text-[10px] text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded">Prix a confirmer</span>}
                      {p.unit_price > 0 && <span className="text-xs font-mono text-slate-600">{(p.quantity * p.unit_price).toFixed(2)} CHF</span>}
                      <Button variant="ghost" size="sm" onClick={() => deletePart(p.id)} className="text-red-400 hover:text-red-600 h-7 w-7 p-0" data-testid={`delete-part-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* ====== BONS DE LIVRAISON (photo -> toutes les pièces dans la liste) ====== */}
        <OrderSlipsSection woId={woId} workOrder={wo} uploadedBy={mechanicName || 'Atelier'} onImported={loadData} />

        <FluidsSection woId={woId} wo={wo} disabled={wo.status === 'completed'} />

        {/* ====== CONTROLE DES NIVEAUX ====== */}
        <Card className="p-4" data-testid="atelier-liquides-section">
          <div className="flex items-center gap-2 mb-3">
            <Droplets className="w-4 h-4 text-blue-500" />
            <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Contrôle des niveaux</h3>
          </div>
          <div className="space-y-2">
            {LIQUIDES_ITEMS.map(item => {
              const val = liquides[item.key];
              const status = typeof val === 'object' ? val?.status : val;
              const quantity = typeof val === 'object' ? val?.quantity || '' : '';
              const isAppoint = status === 'appoint';
              return (
                <div key={item.key} data-testid={`atelier-liquide-${item.key}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-700 flex-1">{item.label}</span>
                    <div className="flex gap-1.5">
                      {LIQUIDE_LEVELS.map(level => (
                        <button key={level.value} type="button"
                          onClick={() => {
                            if (wo.status === 'completed') return;
                            const current = status;
                            if (current === level.value) {
                              updateLiquide(item.key, null);
                            } else if (level.value === 'appoint') {
                              updateLiquide(item.key, { status: 'appoint', quantity: '' });
                            } else {
                              updateLiquide(item.key, level.value);
                            }
                          }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            status === level.value ? `${level.color} text-white shadow-sm` : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          } ${wo.status === 'completed' ? 'cursor-default' : 'cursor-pointer'}`}
                          data-testid={`atelier-liquide-${item.key}-${level.value}`}>
                          {level.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {isAppoint && (
                    <div className="mt-1.5 ml-4 flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        value={quantity}
                        onChange={(e) => updateLiquide(item.key, { status: 'appoint', quantity: e.target.value })}
                        placeholder="0.0"
                        className="h-8 text-sm w-24 font-mono"
                        disabled={wo.status === 'completed'}
                        data-testid={`atelier-liquide-${item.key}-qty`}
                      />
                      <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded">L</span>
                      <span className="text-xs text-slate-400">quantite utilisee</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Atelier Notes */}
        <Card className="p-4" data-testid="atelier-notes-section">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle className="w-4 h-4 text-orange-500" />
            <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Notes atelier</h3>
          </div>

          {atelierNotes.length > 0 && (
            <div className="space-y-2 mb-4">
              {atelierNotes.map((n, i) => (
                <div key={i} className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                  <p className="text-sm text-slate-700">{n.content}</p>
                  <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString('fr-CH')}</p>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Commentaire, observation, produit necessaire..."
              className="flex-1 border rounded-lg p-2.5 text-sm h-20 resize-none"
              data-testid="atelier-note-input"
            />
          </div>
          <div className="flex justify-end mt-2">
            <Button size="sm" onClick={sendNote} disabled={!note.trim() || sending} data-testid="send-atelier-note">
              <Send className="w-3.5 h-3.5 mr-1" /> {sending ? 'Envoi...' : 'Envoyer'}
            </Button>
          </div>
        </Card>

        {/* ====== QUALITY CHECKS ====== */}
        {wo.status !== 'completed' && (
          <Card className="p-4 border-2 border-slate-200" data-testid="quality-checks-section">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-blue-500" /> Controle qualite
            </h3>
            <div className="space-y-3">
              {/* Vehicle tested by workshop manager */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50" data-testid="quality-tested">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={!!wo.quality_tested_by}
                    onCheckedChange={async (checked) => {
                      if (checked) {
                        if (!mechanicName.trim()) { toast.error('Entrez votre nom d\'abord'); return; }
                        try {
                          await axios.put(`${API}/work-orders/${woId}`, {
                            quality_tested_by: mechanicName.trim(),
                            quality_tested_at: new Date().toISOString(),
                          });
                          toast.success('Vehicule marque comme teste');
                          loadData();
                        } catch { toast.error('Erreur'); }
                      } else {
                        try {
                          await axios.put(`${API}/work-orders/${woId}`, {
                            quality_tested_by: null,
                            quality_tested_at: null,
                          });
                          loadData();
                        } catch { toast.error('Erreur'); }
                      }
                    }}
                    data-testid="quality-tested-checkbox"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-blue-500" /> Vehicule teste par le responsable atelier
                    </p>
                    {wo.quality_tested_by && (
                      <p className="text-xs text-emerald-600 font-medium mt-0.5">
                        Par {wo.quality_tested_by} — {wo.quality_tested_at ? new Date(wo.quality_tested_at).toLocaleString('fr-CH') : ''}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {/* Vehicle cleaned */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50" data-testid="quality-cleaned">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={!!wo.quality_cleaned_by}
                    onCheckedChange={async (checked) => {
                      if (checked) {
                        if (!mechanicName.trim()) { toast.error('Entrez votre nom d\'abord'); return; }
                        try {
                          await axios.put(`${API}/work-orders/${woId}`, {
                            quality_cleaned_by: mechanicName.trim(),
                            quality_cleaned_at: new Date().toISOString(),
                          });
                          toast.success('Vehicule marque comme nettoye');
                          loadData();
                        } catch { toast.error('Erreur'); }
                      } else {
                        try {
                          await axios.put(`${API}/work-orders/${woId}`, {
                            quality_cleaned_by: null,
                            quality_cleaned_at: null,
                          });
                          loadData();
                        } catch { toast.error('Erreur'); }
                      }
                    }}
                    data-testid="quality-cleaned-checkbox"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                      <Sparkles className="w-4 h-4 text-violet-500" /> Vehicule nettoye, aspire
                    </p>
                    {wo.quality_cleaned_by && (
                      <p className="text-xs text-emerald-600 font-medium mt-0.5">
                        Par {wo.quality_cleaned_by} — {wo.quality_cleaned_at ? new Date(wo.quality_cleaned_at).toLocaleString('fr-CH') : ''}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Show quality checks status if completed */}
        {wo.status === 'completed' && (wo.quality_tested_by || wo.quality_cleaned_by) && (
          <Card className="p-4" data-testid="quality-checks-completed">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Controle qualite</h3>
            <div className="space-y-1.5">
              {wo.quality_tested_by && (
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <ShieldCheck className="w-4 h-4" />
                  <span>Teste par {wo.quality_tested_by}</span>
                </div>
              )}
              {wo.quality_cleaned_by && (
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <Sparkles className="w-4 h-4" />
                  <span>Nettoye par {wo.quality_cleaned_by}</span>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* VEHICULE PRET */}
        {wo.status !== 'completed' && (
          <Card className={`p-5 border-2 ${wo.atelier_ready ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'}`} data-testid="vehicle-ready-section">
            {wo.atelier_ready ? (
              <div className="text-center space-y-3">
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  <span className="text-lg font-bold text-emerald-700">Vehicule marque PRET</span>
                </div>
                <p className="text-sm text-emerald-600">
                  Marque par {wo.atelier_ready_by || 'Atelier'} le {wo.atelier_ready_at ? new Date(wo.atelier_ready_at).toLocaleString('fr-CH') : ''}
                </p>
                <p className="text-xs text-slate-500">La reception a ete informee (résumé dans la fiche client).</p>
                <Button variant="outline" size="sm" onClick={undoVehicleReady} disabled={markingReady} data-testid="undo-vehicle-ready-btn" className="text-slate-500">
                  <Undo2 className="w-3.5 h-3.5 mr-1" /> Annuler
                </Button>
                <div className="text-left"><ReadySummary s={wo.ready_summary} /></div>
              </div>
            ) : (
              <div className="text-center space-y-3">
                <p className="text-sm text-slate-600 font-medium">Les travaux sont termines ?</p>
                <Button
                  size="lg"
                  onClick={markVehicleReady}
                  disabled={markingReady || !mechanicName.trim()}
                  data-testid="mark-vehicle-ready-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 text-base font-bold shadow-lg"
                >
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  {markingReady ? 'En cours...' : 'VEHICULE PRET'}
                </Button>
                <p className="text-xs text-slate-400">Un signal sonore informera la reception.</p>
              </div>
            )}
          </Card>
        )}
      </div>
    </AtelierLayout>
  );
};

export default AtelierWorkOrderDetail;
